import { useEffect, useState } from "react";
import { api, formatCurrency, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import HelpTip from "../components/Help/HelpTip";
import EmptyState from "../components/Help/EmptyState";
import { Plus, Repeat, CheckCheck, Trash2, Calendar, Check } from "lucide-react";

import { toast } from "sonner";
import { Field, SelectField } from "./Properties";

const CATEGORIES_EXPENSE = [
  "Advertising", "Auto & Travel", "Cleaning & Maintenance", "Commissions", "Insurance",
  "Legal & Professional", "Management Fees", "Mortgage Interest", "Other Interest",
  "Repairs", "Supplies", "Taxes", "Utilities", "Depreciation", "Other"
];

const CATEGORIES_INCOME = [
  "Rent", "STR Booking", "Late Fee", "Pet Fee", "Other Income"
];

export default function Recurring() {
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    property_id: "",
    tenant_id: "",
    vendor_id: "",
    type: "Expense",
    category: "Utilities",
    amount: "",
    frequency: "monthly",
    next_due_date: new Date().toISOString().slice(0, 10),
    auto_approve: false,
    notes: "",
  });
  const { can } = useAuth();

  const load = () => {
    api.get("/recurring").then((r) => setItems(r.data));
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/tenants").then((r) => setTenants(r.data));
    api.get("/vendors").then((r) => setVendors(r.data));
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Enter valid amount");

    try {
      await api.post("/recurring", {
        ...form,
        amount: amt,
        property_id: form.property_id || null,
        tenant_id: form.tenant_id || null,
        vendor_id: form.vendor_id || null,
      });
      toast.success("Recurring rule created");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error("Failed to create rule");
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/recurring/${id}/approve`);
      toast.success("Transaction recorded and due date updated");
      load();
    } catch (e) {
      toast.error("Failed to approve");
    }
  };

  const bulkApprove = async () => {
    try {
      const r = await api.post("/recurring/bulk-approve");
      toast.success(`Approved ${r.data.approved_count} due transactions`);
      load();
    } catch (e) {
      toast.error("Bulk approve failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this recurring rule?")) return;
    try {
      await api.delete(`/recurring/${id}`);
      toast.success("Rule deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const dueCount = items.filter((i) => i.next_due_date <= todayStr).length;

  return (
    <Layout
      title="Recurring Transactions"
      subtitle={`${items.length} automated schedule rules`}
      actions={
        can.edit && (
          <div className="flex gap-2">
            {dueCount > 0 && (
              <button
                onClick={bulkApprove}
                data-testid="bulk-approve-btn"
                className="btn-primary bg-[#34C759] hover:bg-[#2db84f] text-sm py-2 px-3 flex items-center gap-1.5"
              >
                <CheckCheck size={16} /> Approve All Due ({dueCount})
              </button>
            )}
            <button
              onClick={() => setShowForm(!showForm)}
              data-testid="new-recurring-btn"
              className="btn-primary text-sm py-2 px-4 flex items-center gap-1.5"
            >
              <Plus size={16} /> New Rule
            </button>
          </div>
        )
      }
    >
      {/* Due Banner */}
      {dueCount > 0 && (
        <div className="card p-4 mb-6 bg-[#E8F8EE] border-[#34C759]/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Repeat className="text-[#1f8e3a]" size={20} />
            <div className="text-sm">
              <span className="font-semibold text-[#1f8e3a]">{dueCount} recurring transactions</span>
              <span className="text-[#1D1D1F]"> are due for approval.</span>
            </div>
          </div>
          {can.edit && (
            <button onClick={bulkApprove} className="text-sm font-semibold text-[#1f8e3a] underline hover:no-underline">
              Approve all now →
            </button>
          )}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="card p-6 mb-6" data-testid="recurring-form">
          <h3 className="text-lg font-bold mb-4">Create Recurring Rule</h3>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectField
              label="Type"
              value={form.type}
              onChange={(v) => setForm({ ...form, type: v, category: v === "Income" ? "Rent" : "Utilities" })}
              options={["Expense", "Income"]}
              testId="rec-type"
            />
            <SelectField
              label="Category"
              value={form.category}
              onChange={(v) => setForm({ ...form, category: v })}
              options={form.type === "Income" ? CATEGORIES_INCOME : CATEGORIES_EXPENSE}
              testId="rec-category"
            />
            <Field label="Amount" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} required testId="rec-amount" />
            <SelectField
              label="Frequency"
              value={form.frequency}
              onChange={(v) => setForm({ ...form, frequency: v })}
              options={["weekly", "monthly", "yearly"]}
              testId="rec-frequency"
              helpTopic="recurring-transactions"
            />
            <Field label="Next Due Date" type="date" value={form.next_due_date} onChange={(v) => setForm({ ...form, next_due_date: v })} required testId="rec-due" helpTopic="recurring-transactions" />
            <div>
              <label className="label">Property</label>
              <select
                className="lapen-input"
                value={form.property_id}
                onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                data-testid="rec-property"
              >
                <option value="">— Select Property —</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            {form.type === "Income" ? (
              <div>
                <label className="label">Tenant (optional)</label>
                <select
                  className="lapen-input"
                  value={form.tenant_id}
                  onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
                >
                  <option value="">— None —</option>
                  {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label className="label">Vendor (optional)</label>
                <select
                  className="lapen-input"
                  value={form.vendor_id}
                  onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
                >
                  <option value="">— None —</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
            )}
            <Field label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="rec-notes" />

            <div className="sm:col-span-2 flex gap-3 mt-2">
              <button type="submit" className="btn-primary" data-testid="rec-submit">Save Rule</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-3 text-[#86868B] font-semibold">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      <div className="card divide-y divide-[#E5E5EA]/60" data-testid="recurring-list">
        {items.length === 0 && (
          <div className="p-4">
            <EmptyState
              icon={Repeat}
              title="No Recurring Schedules Yet"
              description="Automate repeating transactions like mortgages, quarterly insurance, or annual property taxes with 1-click manual approval."
              helpTopic="recurring-transactions"
              helpText="About Recurring Schedules & Approval"
              actionLabel="+ Create First Schedule"
              onAction={() => setShowForm(true)}
              testId="recurring-empty-state"
            />
          </div>
        )}

        {items.map((r) => {
          const isDue = r.next_due_date <= todayStr;
          return (
            <div key={r.id} className="p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`recurring-item-${r.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`tag ${r.type === "Income" ? "tag-success" : "tag-danger"}`}>{r.type}</span>
                  <span className="font-bold text-base">{r.category}</span>
                  <span className="tag capitalize">{r.frequency}</span>
                </div>
                <div className="text-xs text-[#86868B] mt-1.5 flex items-center gap-2">
                  <span>{r.property_name || "All properties"}</span>
                  <span>·</span>
                  <span>Next: {formatDate(r.next_due_date)}</span>
                  {r.tenant_name && <span>· Tenant: {r.tenant_name}</span>}
                  {r.vendor_name && <span>· Vendor: {r.vendor_name}</span>}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className={`text-lg font-extrabold ${r.type === "Income" ? "text-[#1f8e3a]" : "text-[#1D1D1F]"}`}>
                    {r.type === "Income" ? "+" : "−"}{formatCurrency(r.amount)}
                  </div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#86868B]">{r.frequency}</div>
                </div>

                {can.edit && (
                  <button
                    onClick={() => approve(r.id)}
                    data-testid={`approve-btn-${r.id}`}
                    className={`text-xs font-semibold py-2 px-3 rounded-full flex items-center gap-1 ${
                      isDue ? "btn-primary" : "bg-[#F5F5F7] text-[#1C3A5E] hover:bg-[#E5E5EA]"
                    }`}
                  >
                    <Check size={14} /> {isDue ? "Approve Due" : "Post Now"}
                  </button>
                )}

                {can.delete && (
                  <button onClick={() => remove(r.id)} className="p-1.5 text-[#86868B] hover:text-[#c1271d]">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
